import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, EventCard, FloatingActionButton, LoadingSpinner } from '../components/ui';
import { useFilters } from '../contexts/FilterContext';
import { useAuth } from '../hooks/useAuth';
import { useCategories } from '../hooks/useCategories';
import { useEvents } from '../hooks/useEvents';
import { trackEventSearch, trackFilterApplied } from '../services/analyticsService';

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { filters, activeEventFilter, activeCategoryFilter, setActiveEventFilter, setActiveCategoryFilter } = useFilters();
  const [searchTerm, setSearchTerm] = useState('');
  const { categories } = useCategories({ activeOnly: true });

  const { events, loading, error, searchEvents } = useEvents({
    filters: filters,
    initialLimit: 12,
    realTime: true
  });

  // Helper function to get category display name
  const getCategoryDisplayName = (categoryId: string | null): string | null => {
    if (!categoryId) return null;
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : null;
  };

  // Check if any filters are active
  const hasActiveFilters = activeEventFilter !== 'All Events' || activeCategoryFilter !== null;

  const handleSearch = async (term: string) => {
    setSearchTerm(term);
    if (term.trim()) {
      await searchEvents(term);
      trackEventSearch({
        query: term,
        results_count: events.length,
      });
      // Clear active filters when searching
      if (activeEventFilter !== 'All Events') {
        setActiveEventFilter('All Events');
      }
      if (activeCategoryFilter !== null) {
        setActiveCategoryFilter(null);
      }
    }
  };

  const handleEventFilterClick = (filter: string) => {
    setActiveEventFilter(filter);
    setSearchTerm('');

    // Track filter usage
    if (filter === 'Free Events') {
      trackFilterApplied({ filter_type: 'free' });
    } else if (filter === 'Today') {
      trackFilterApplied({ filter_type: 'today' });
    } else if (filter === 'This Weekend') {
      trackFilterApplied({ filter_type: 'weekend' });
    }
  };

  return (
    <div className="px-4 sm:px-6 xl:px-0 pb-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Discover Los Angeles Events</h1>
        <p className="text-gray-600 mt-2">
          {hasActiveFilters ? (
            <>
              Showing: {activeEventFilter !== 'All Events' && activeEventFilter}
              {activeEventFilter !== 'All Events' && activeCategoryFilter && ' & '}
              {activeCategoryFilter && getCategoryDisplayName(activeCategoryFilter)}
            </>
          ) : (
            'Find amazing events happening around you'
          )}
        </p>
      </div>

      {/* Search and Filters Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="input-field flex-1"
          />
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={activeEventFilter === 'All Events' ? 'primary' : 'outline'}
              onClick={() => handleEventFilterClick('All Events')}
            >
              All
            </Button>
            <Button
              variant={activeEventFilter === 'Free Events' ? 'primary' : 'outline'}
              onClick={() => handleEventFilterClick('Free Events')}
            >
              Free
            </Button>
            <Button
              variant={activeEventFilter === 'Today' ? 'primary' : 'outline'}
              onClick={() => handleEventFilterClick('Today')}
            >
              Today
            </Button>
            <Button
              variant={activeEventFilter === 'This Weekend' ? 'primary' : 'outline'}
              onClick={() => handleEventFilterClick('This Weekend')}
            >
              Weekend
            </Button>
          </div>
        </div>
        
        {/* Active Filter Indicator */}
        {hasActiveFilters && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600">Active filters:</span>
            {activeEventFilter !== 'All Events' && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {activeEventFilter}
                <button
                  onClick={() => handleEventFilterClick('All Events')}
                  className="ml-2 hover:text-primary-900"
                >
                  ×
                </button>
              </span>
            )}
            {activeCategoryFilter && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                {getCategoryDisplayName(activeCategoryFilter)}
                <button
                  onClick={() => setActiveCategoryFilter(null)}
                  className="ml-2 hover:text-primary-900"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Events Grid */}
      {loading && events.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <LoadingSpinner size="lg" text="Loading events..." />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
          <h2 className="text-xl font-semibold text-red-800 mb-4">Error Loading Events</h2>
          <p className="text-red-600 mb-6">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">No Events Found</h2>
          <p className="text-gray-600 mb-6">
            {user
              ? 'Be the first to add an event to your community!'
              : 'No events available right now. Sign in to create the first event!'
            }
          </p>
          {user ? (
            <Button onClick={() => navigate('/add-event')}>
              Create Event
            </Button>
          ) : (
            <Button onClick={() => navigate('/login')}>
              Sign In to Create Events
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onPress={() => navigate(`/events/${event.id}`)}
            />
          ))}
        </div>
      )}

      {/* Floating Action Button - only show on mobile and if user is authenticated */}
      {user && (
        <FloatingActionButton
          onClick={() => navigate('/add-event')}
          aria-label="Create new event"
        />
      )}
    </div>
  );
}